/**
 * API 응답 타입 정의
 * 실제 백엔드 API 응답 구조와 일치하도록 정의
 * API 문서: https://admin.dev.olulo.com.mx/docs
 */

import type { SessionBill } from './split-payment';

// 공통 응답 래퍼
export interface ApiResponse<T> {
  success?: boolean;
  data: T;
  message?: string;
  error_code?: string;
}

/**
 * 공통 페이지네이션 (Laravel paginate() meta 구조와 일치)
 * @see Laravel Resource Collection의 meta 필드
 */
export interface PaginationMeta {
  current_page: number;
  from: number | null;
  last_page: number;
  per_page: number;
  to: number | null;
  total: number;
}

/**
 * @deprecated PaginationMeta 사용. Laravel 실제 응답과 필드명이 다름.
 */
export interface PaginationInfo {
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

/**
 * Laravel paginate() links 구조
 * { first, last, prev, next }
 */
export interface PaginationLinks {
  first: string | null;
  last: string | null;
  prev: string | null;
  next: string | null;
}

/**
 * Laravel Resource Collection paginate() 응답 구조
 * { data: T[], links: {...}, meta: PaginationMeta }
 */
export interface PaginatedResponse<T> {
  data: T[];
  links?: PaginationLinks;
  meta?: PaginationMeta;
  /** @deprecated meta 사용 */
  pagination?: PaginationInfo;
}

// ==================== 조직 (Organization) ====================
export interface Organization {
  id: number;
  slug: string;
  nickname: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  contact_email: string;
  contact_phone: string;
  is_active: boolean;
  stores_count: number;
  created_at: string;
  updated_at: string;
}

export interface OrganizationSummary {
  id: number;
  slug: string;
  /** 일부 BE 응답(BrandResource 등)에서는 nickname이 포함되지 않음 */
  nickname?: string;
  name: string;
  logo_url?: string | null;
}

export interface OrganizationListResponse {
  data: Organization[];
}

// ==================== 브랜드 (Brand) ====================
export interface Brand {
  id: number;
  slug: string;
  nickname?: string | null;
  name: string;
  description?: string;
  logo_url?: string;
  is_active?: boolean;
  stores_count?: number;
  stores?: Store[];
}

export interface BrandDetailResponse {
  data: Brand & {
    organization?: OrganizationSummary;
  };
}

export interface BrandListByOrgResponse {
  success: boolean;
  data: {
    organization: OrganizationSummary;
    brands: Brand[];
  };
}

export interface BrandStoreListResponse {
  success: boolean;
  data: {
    organization: OrganizationSummary;
    brand: {
      id: number;
      slug: string;
      name: string;
    };
    stores: Store[];
  };
}

// ==================== 매장 (Store) ====================

/** 요일 (백엔드 DAY_NAMES 상수와 일치) */
export type DayOfWeek = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

export interface DayHours {
  open: string | null;
  close: string | null;
  /**
   * 요일별 정기 휴무 여부 (#651 / admin#1849 — additive).
   *
   * 계약: 휴무 요일도 open/close 시간이 **보존**되어 내려오므로 (토글 해제 시 복원용)
   * 시간 부재 여부로 휴무를 추론할 수 없다. `is_24h=true` 매장은 admin 측에서
   * 항상 `false` 로 마스킹 — FE 별도 처리 불필요.
   * 구버전 응답에는 필드 부재 (undefined) → 기존 동작 유지 (fail-safe).
   * 표시 전용 — 주문 가능 판정은 `is_open_now`/`closure_reason` 서버 권위.
   */
  is_closed?: boolean;
}

export type StoreHours = Partial<Record<DayOfWeek, DayHours>>;

/** 백엔드 EntityStatus enum — 엔티티 승인/운영 상태 */
export type EntityStatus = 'pending' | 'approved' | 'temporarily_open' | 'temporarily_closed' | 'suspended' | 'rejected';

/**
 * 매장 휴무 사유 (Q7 박제 옵션 C — admin#1230 / #1298)
 *
 * BE `StoreResource.closure_reason` 응답.
 * FE 는 이 값을 그대로 라벨 매트릭스에 매핑하며, raw `operating_hours` 재해석 금지.
 *
 * @see olulo-mx-admin/app/Services/StoreHoursService.php::getClosureReason
 * @see menu#347
 */
export type StoreClosureReason =
  | 'temporarily_closed' // status === temporarily_closed (관리자 임시 휴무)
  | 'suspended'          // status === suspended (관리자 정지)
  | 'outside_hours'      // status === approved 인데 영업시간 외
  | 'unavailable';       // 기타 비가용 (pending/rejected 등)

/**
 * 결제 수단 조합 카탈로그 전용 method 축.
 * BE `payment_method_combinations.method` PG enum ('card' | 'cash') 과 1:1 대응.
 *
 * 주의: 같은 파일의 `PaymentMethod` ('card' | 'cash' | 'transfer' | 'wallet') 와 다름.
 * - `PaymentMethod`: 실제 결제 레코드의 수단 (transfer/wallet 포함)
 * - `PaymentMethodKind`: 조합 카탈로그의 method 축 (card/cash 만)
 * 관계: PaymentMethod ⊇ PaymentMethodKind
 *
 * CheckoutPage 에서 조합 선택 UI 에는 PaymentMethodKind,
 * CreatePaymentRequest.method 에는 PaymentMethod 사용.
 */
export type PaymentMethodKind = 'card' | 'cash';

/**
 * 결제 채널 축 (BE `payment_method_combinations.channel` PG enum).
 * - platform: 플랫폼 PG (Bloomin/OpenLinea) 경유
 * - pos: 매장 POS 단말 (Clip / Mercado Pago 등)
 * - manual: 현금·이체 등 수기 확정
 */
export type PaymentChannelKind = 'platform' | 'pos' | 'manual';

/**
 * 결제 시점 축 (BE `payment_method_combinations.timing` PG enum).
 * - prepay: 선결제 (주문 생성 시점 결제 완료 또는 의도)
 * - postpay: 후결제 (식사 후 매장에서 결제 처리)
 */
export type PaymentTimingKind = 'prepay' | 'postpay';

/**
 * 결제 수단 조합 카탈로그 (BE `payment_method_combinations` 테이블).
 * 공개 카탈로그 API `GET /api/v1/payment-method-combinations` 응답 요소.
 * method × channel × timing 3축 정규화.
 */
export interface PaymentMethodCombination {
  id: number;
  /** 안정 식별 코드 (예: 'card_platform_prepay') */
  code: string;
  method: PaymentMethodKind;
  channel: PaymentChannelKind;
  timing: PaymentTimingKind;
  /** i18n 키 (예: 'payment.combinations.card_platform_prepay') — FE 에서 lazy resolve */
  label_key: string;
  /** 결제 게이트웨이 연동 필요 여부 */
  requires_gateway: boolean;
  /** 기본 수동 확정 정책 (매장별 override 없을 때 사용) */
  default_manual_confirm: boolean;
  /** 렌더링 정렬 순서 (오름차순) */
  sort_order: number;
}

/**
 * 결제 조합 할당의 주문유형 scope (admin `PaymentAssignmentScope` enum, #1705 R1 / D1-a).
 *
 * - `all`: 주문유형 무관 — 픽업/테이블 양쪽에 노출 (기존 store 단위 할당 = R1 백필 기본값).
 * - `pickup`/`table`: 해당 주문유형에만 노출.
 *
 * 필터 의미는 admin 의 `where order_type IN ('all', orderType)` 와 정합:
 * `'all'` 또는 현재 orderType 과 일치할 때만 노출.
 */
export type PaymentAssignmentScope = 'all' | 'pickup' | 'table';

/**
 * 매장이 허용한 결제 수단 조합 할당 (BE `store_payment_method` junction 테이블).
 *
 * P3-1: BE 가 junction.manual_confirm 이 NULL 이면 combination.default_manual_confirm 으로
 * resolve 하여 항상 boolean 으로 내려보냄. FE 는 nullable 고려 불필요.
 */
export interface StorePaymentMethodAssignment {
  combination_id: number;
  manual_confirm: boolean;
  /**
   * 주문유형 scope (#1705 R1 / D1-a). admin `StoreResource` 가 R1 이후 항상 포함.
   *
   * optional 인 이유: R1 배포 이전 페이로드 / 캐시된 stale 응답 호환.
   * R1 백필이 'all' 이므로 실제 API 응답은 항상 이 필드를 'all'/'pickup'/'table' 로 포함한다.
   *
   * fail-closed 경계 (Codex P2): `computeAvailableCombinations` 는
   *   - `undefined`(필드 부재) → `'all'` 간주 (무회귀, 모든 주문유형 노출)
   *   - `null`(명시적 null) / 인식 불가 값 → fail-closed (미노출)
   * 로 구분 처리. 변조/계약 위반 페이로드가 `null` 로 들어올 수 있으므로 타입에 명시.
   */
  order_type?: PaymentAssignmentScope | null;
}

export interface Store {
  id: number;
  slug: string;
  /** 매장 닉네임 (@ 제외, @nickname으로 접근 가능) */
  nickname?: string | null;
  name: string;
  description?: string | null;
  /** 픽업 위치 안내 (예: "1층 카운터", "주차장 앞") */
  pickup_location?: string | null;
  latitude: number | null;
  longitude: number | null;
  image_url: string | null;
  rating: number;
  review_count: number;
  /** 거리 (km) - 위치 기반 검색 시에만 포함 */
  distance?: number;
  address: string;
  phone: string;
  pickup_available: boolean;
  status: EntityStatus;
  hours?: StoreHours;
  is_open_now?: boolean;
  /**
   * 매장 휴무 사유 (Q7 박제 옵션 C — admin#1230 / #1298).
   * BE `StoreResource` 가 `hours` relation 로드 시에만 반환. 그 외 undefined.
   *
   * 주의: undefined (Q7 hours payload 미로드) 와 null (로드됐으나 사유 없음) 은
   * 의미가 다름. transformer 는 두 케이스를 구분 보존해야 함 (P1-1 회귀 게이트).
   */
  closure_reason?: StoreClosureReason | null;
  /**
   * 다음 영업 가능 시각 (ISO8601, store timezone 기준) (Q7 박제 옵션 C).
   * BE `StoreResource` 가 `hours` relation 로드 시에만 반환. 그 외 undefined.
   *
   * 주의: undefined / null 의미 구분은 closure_reason 과 동일.
   */
  next_available_at?: string | null;
  /** 매장별 세율 (퍼센트, 예: 16.00 = 16%) - 백엔드에서 퍼센트로 내려옴 */
  tax_rate?: number | null;
  /** 24시간 영업 여부 */
  is_24h?: boolean;
  /** 매장 기본 통화 (예: 'MXN', 'USD') */
  currency?: string | null;
  /**
   * 매장 IANA 타임존 (#1128: BE PR #1129).
   * 멕시코 4개 TZ 중 하나: America/Mexico_City | Cancun | Mazatlan | Tijuana
   */
  timezone?: string | null;
  /**
   * 매장이 허용한 결제 수단 조합 (#217/#218, β-1b).
   * BE `StoreResource` 가 junction 테이블 기반으로 resolve.
   * β-1b 단계에서는 타입만 선언, 소비는 β-2b (CheckoutPage) 에서 시작.
   */
  accepted_payment_methods?: StorePaymentMethodAssignment[];
  /**
   * menu 홈 "픽업 매장 찾기" 리스트 노출 여부(admin#2172/menu#804).
   * false 여도 직접 URL·QR·`/fila/{slug}` 진입은 유지된다 — BE 가 홈 리스트 조회에만
   * 필터를 적용한다. 구버전 BE 응답 호환을 위해 optional(값 부재는 기존 노출 동작으로
   * 방어적으로 취급).
   */
  pickup_discoverable?: boolean;
  /**
   * true 면 앱 픽업 주문에 픽업 줄 called 상태 waitlist 토큰이 필수다(admin#2172/menu#804).
   * @see docs/plans/2026-07-24-pickup-discovery-waitlist-gate-design.md §3
   */
  pickup_requires_waitlist?: boolean;
}

/**
 * 매장 포인트 정책 (BE: GET /api/v1/stores/{id}.points)
 *
 * #285: FE 는 enabled 한 필드로 포인트 UI 전면 게이팅.
 * 다른 필드는 활성 시 계산/표시에 사용됨.
 *
 * @see .claude/specs/SPEC-285-points-ui-gating.md
 */
export interface StorePointsConfig {
  /** 포인트 적립·사용 통합 활성 플래그 */
  enabled: boolean;
  /** 적립률 (비율 0.10 = 10%) */
  earn_rate: number;
  /** 최소 사용 포인트 */
  min_use_points: number;
  /** 주문액 대비 최대 사용 비율 (0.50 = 50%) */
  max_use_ratio: number;
  /** 적립 후 만료일수 */
  expiry_days: number;
}

export interface StoreDetail extends Store {
  platform_card_prepay_availability?: 'unavailable' | 'unknown';
  organization?: OrganizationSummary;
  brand?: Brand;
  /** #285 매장 포인트 정책. 구버전 BE 대응으로 optional */
  points?: StorePointsConfig;
}

export interface StoreListByOrgResponse {
  success: boolean;
  data: {
    organization: OrganizationSummary;
    stores: Store[];
  };
}

export interface StoreWithOrganization extends Store {
  organization: OrganizationSummary;
}

export interface AllStoresResponse {
  success: boolean;
  data: {
    stores: StoreWithOrganization[];
    meta: {
      total_stores: number;
      total_organizations: number;
    };
  };
}

export interface StoreDetailResponse {
  data: StoreDetail;
}

// ==================== 메뉴 할인 (Menu Discount) ====================

/**
 * 메뉴 할인 정보
 * @see olulo-mx-admin/app/Http/Resources/MenuResource.php:98-104
 */
export interface MenuDiscount {
  id: number;
  name: string;
  type: 'percentage' | 'fixed_amount';
  value: number;
  /** 실제 할인 금액 (original_price - price) */
  amount: number;
}

/**
 * 메뉴 가용성 사유 (BE `MenuHoursService::getAvailabilityStatus` 반환 reason)
 *
 * - 비가용 사유: `sold_out` · `inactive` · `outside_menu_hours` · `outside_category_hours` · `store_closed`
 * - 가용 사유: `within_menu_hours` · `within_category_hours` · `no_store_restriction` · `store_open`
 *
 * @see olulo-mx-admin/app/Services/MenuHoursService.php
 */
export type MenuUnavailableReason =
  | 'sold_out'
  | 'inactive'
  | 'outside_menu_hours'
  | 'outside_category_hours'
  | 'store_closed';

export type MenuAvailableReason =
  | 'within_menu_hours'
  | 'within_category_hours'
  | 'no_store_restriction'
  | 'store_open';

export type MenuAvailabilityReason = MenuUnavailableReason | MenuAvailableReason;

/**
 * 메뉴 가용성 정보
 * @see olulo-mx-admin/app/Http/Resources/MenuResource.php:167-171
 */
export interface MenuAvailabilityInfo {
  available: boolean;
  reason: MenuAvailabilityReason | null;
  next_available_at: string | null;
  time_slots: Array<{ from: string | null; until: string | null }>;
}

// ==================== 메뉴 카테고리 (Menu Category) ====================
export interface MenuCategory {
  id: number;
  name: string;
  description: string;
  display_order: number;
  is_available: boolean;
}

// ==================== 메뉴 (Menu) ====================
export interface MenuOption {
  id: number;
  name: string;
  /** BE MenuOptionGroup.input_type 값 (MenuOptionInputType enum) */
  type: MenuOptionInputType | null;
  is_required: boolean;
  min_selections: number | null;
  max_selections: number | null;
  options: MenuOptionChoice[];
}

export interface MenuOptionChoice {
  id: number;
  name: string;
  price: number;
  quantity_limit?: number | null;
  is_default: boolean;
}

export interface Menu {
  id: number;
  name: string;
  description: string | null;
  price: number;
  /** 할인 적용 시 원가 (할인 없으면 null) */
  original_price: number | null;
  /** 적용된 할인 정보 (할인 없으면 null) */
  discount: MenuDiscount | null;
  currency: string;
  category: string | null;
  category_id: number | null;
  image_url: string | null;
  /**
   * 판매 설정 (BE MenuResource: status === MenuStatus::Active).
   * false여도 SoldOut/Inactive 둘 다 가능 — 품절 여부 판정은 `is_sold_out`을 사용하라.
   */
  is_available: boolean;
  /** 품절 여부 (BE MenuResource: status === MenuStatus::SoldOut). 명시적 품절에만 true. */
  is_sold_out?: boolean;
  store_id: number;
  options: MenuOption[];
  allergens: string[];
  calories: number | null;
  spice_level: number | null;
  is_popular: boolean;
  is_set_menu: boolean;
  /** 세트 메뉴 구성 아이템 (is_set_menu=true인 경우만 포함) */
  set_menu_items?: SetMenuItem[];
  /** 가용성 정보 (include_availability=true 쿼리 시에만 포함) */
  is_available_at?: MenuAvailabilityInfo;
  /**
   * #1439 G4 — 영업시간 외 표시 모드 (admin#1439 PR 3 BE 추가).
   *
   * - `active`: 영업시간 외에도 정상 노출 + 주문 가능 (default)
   * - `disabled`: 영업시간 외 grayed out + 주문 불가
   * - `hidden`: 영업시간 외 메뉴 카드 자체 미노출 (FE 가 필터)
   */
  display_state?: 'hidden' | 'disabled' | 'active';
}

// ==================== 세트 메뉴 아이템 (SetMenuItem) ====================

/**
 * 세트 메뉴 구성 아이템의 자식 메뉴 정보
 */
export interface SetMenuChildMenu {
  id: number;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  image_url: string | null;
  is_available: boolean;
}

/**
 * 세트 메뉴 구성 아이템의 옵션 그룹 (선택형)
 */
export interface SetMenuOptionGroup {
  id: number;
  name: string;
  description: string | null;
  input_type: MenuOptionInputType;
  is_required: boolean;
  min_select: number | null;
  max_select: number | null;
  sort_order: number;
  options?: ApiMenuOptionChoice[];
}

/**
 * 세트 메뉴 구성 아이템
 */
export interface SetMenuItem {
  id: number;
  /** 구성 유형: fixed(고정), selection(선택) */
  type: 'fixed' | 'selection';
  /** 표시 이름 */
  name: string;
  /** 기본 수량 */
  quantity_default: number;
  /** 필수 여부 */
  is_required: boolean;
  /** 정렬 순서 */
  sort_order: number;
  /** 고정 메뉴인 경우 자식 메뉴 정보 */
  child_menu?: SetMenuChildMenu | null;
  /** 선택형인 경우 옵션 그룹 정보 */
  option_group?: SetMenuOptionGroup | null;
}

export interface MenuStoreSummary {
  id: number;
  name: string;
  slug: string;
}

/**
 * 메뉴 목록 API 응답의 매장 상세 정보
 * 기존 MenuStoreSummary + StoreDetail 필드 포함 (#194)
 *
 * ⚠️ 경고: MenuStoreSummary 만 상속하고 Store 필드를 수동 나열하는 구조.
 * Store 에 새 필드 추가 시 여기에도 명시적으로 추가 필수 (상속 자동 전파 없음).
 * 누락 시 픽업 플로우 (GET /pickup/menus) 응답에서 해당 필드 접근 시 undefined
 * 반환 → fail-closed 무음 발생 가능.
 */
export interface MenuStoreDetail extends MenuStoreSummary {
  address: string;
  phone: string;
  image_url: string | null;
  latitude: number | null;
  longitude: number | null;
  rating: number;
  review_count: number;
  pickup_available: boolean;
  /** 픽업 위치 안내 (예: "1층 카운터", "주차장 앞") */
  pickup_location?: string | null;
  status: EntityStatus;
  hours: StoreHours;
  is_open_now: boolean;
  /** 24시간 영업 여부 */
  is_24h?: boolean;
  /** Q7 박제 옵션 C — admin#1230 / #1298. @see Store.closure_reason */
  closure_reason?: StoreClosureReason | null;
  /** Q7 박제 옵션 C — admin#1230 / #1298. @see Store.next_available_at */
  next_available_at?: string | null;
  organization?: OrganizationSummary;
  brand?: Brand;
  /** 매장별 세율 (퍼센트, 예: 16.00 = 16%) - 백엔드에서 퍼센트로 내려옴 */
  tax_rate?: number | null;
  /** 매장 기본 통화 (예: 'MXN', 'USD') */
  currency?: string | null;
  /**
   * 매장 IANA 타임존 (#1128: BE PR #1129).
   * 멕시코 4개 TZ 중 하나: America/Mexico_City | Cancun | Mazatlan | Tijuana
   */
  timezone?: string | null;
  /** #285 매장 포인트 정책 (BE #1142). 구버전 BE 대응으로 optional */
  points?: StorePointsConfig;
  /**
   * 매장이 허용한 결제 수단 조합 (#217/#218, β-1b).
   * Store 에 추가된 동일 필드를 MenuStoreDetail 수동 나열 구조상 명시 중복 선언.
   */
  accepted_payment_methods?: StorePaymentMethodAssignment[];
  /** @see Store.pickup_discoverable (admin#2172/menu#804) */
  pickup_discoverable?: boolean;
  /** @see Store.pickup_requires_waitlist (admin#2172/menu#804) */
  pickup_requires_waitlist?: boolean;
}

export interface MenuListMeta {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export interface MenuListResponse {
  success: boolean;
  data: {
    store: MenuStoreDetail;
    categories: MenuCategory[];
    menus: Menu[];
  };
  meta?: MenuListMeta;
}

export interface MenuDetailResponse {
  data: Menu;
}

// ==================== 주문 (Order) ====================
export interface OrderItem {
  menu_id: number;
  quantity: number;
  options?: OrderItemOption[];
  special_instructions?: string;
}

export interface OrderItemOption {
  option_id: number;
  choice_ids: number[];
  /** 텍스트/숫자 입력 옵션 값 (text_input, number_input, textarea용) */
  input_value?: string;
}

export interface CreateOrderRequest {
  store_id: number;
  order_type: 'pickup' | 'table';
  table_number?: string;
  items: OrderItem[];
  customer_name?: string;
  customer_phone?: string;
  /**
   * 대체 연락처 (#272 Q8 옵션 B): 매장 직원이 customer_phone 대신 우선 통화할 번호.
   * 로그인 사용자가 "다른 번호로 연락 받기" 토글 ON 시 전송. 미설정 시 BE 가
   * effective_contact_phone = customer_phone fallback.
   */
  contact_phone?: string;
  /** 비회원 주문 시 비밀번호 (최소 6자) */
  guest_password?: string;
  notes?: string;
  tip?: number;
  /** Table postpay settlement projection intent. Prepay payments still use createPayment combination_id. */
  combination_id?: number;
  /**
   * 픽업 줄서기 게이트 매장(#804) — `pickup_requires_waitlist=true` 매장에서 필수.
   * localStorage 보관 access_token(`olulo_waitlist_token_{storeId}`)을 그대로 전달한다.
   * 게이트가 꺼진 매장은 값이 와도 서버가 무시한다.
   */
  waitlist_token?: string;
}

/** 주문 취소 요청 */
export interface CancelOrderRequest {
  reason?: string;
  /** 비회원 주문 취소 시 비밀번호 필수 */
  guest_password?: string;
}

/** 비회원 주문 조회 요청 */
export interface GuestOrderLookupRequest {
  phone: string;
  password: string;
}

/**
 * 전화번호 형식 검증
 * - 숫자만 허용
 * - 8-15자리
 */
export function isValidPhoneNumber(phone: string): boolean {
  if (!phone || typeof phone !== 'string') {
    return false;
  }
  // 숫자만 추출
  const digitsOnly = phone.replace(/\D/g, '');
  // 8-15자리 숫자만 허용 (국제 전화번호 고려)
  return digitsOnly.length >= 8 && digitsOnly.length <= 15;
}

/**
 * 비회원 주문 조회 요청 검증
 */
export function validateGuestLookupRequest(
  data: GuestOrderLookupRequest
): { valid: boolean; error?: string } {
  if (!data.phone) {
    return { valid: false, error: '전화번호를 입력해주세요.' };
  }
  if (!isValidPhoneNumber(data.phone)) {
    return { valid: false, error: '올바른 전화번호 형식이 아닙니다.' };
  }
  if (!data.password) {
    return { valid: false, error: '비밀번호를 입력해주세요.' };
  }
  if (data.password.length < 6) {
    return { valid: false, error: '비밀번호는 최소 6자 이상이어야 합니다.' };
  }
  return { valid: true };
}

/** 비회원 주문 조회 응답 */
export interface GuestOrderLookupResponse {
  data: Order[];
  message: string;
}

export interface Order {
  id: number;
  order_number: string | null;
  /** #1919 P4: 영업일 내 POS 짧은 순번 — legacy 는 null/부재 */
  daily_sequence?: number | null;
  store_id: number;
  store_name: string;
  order_type: 'pickup' | 'table';
  /** 주문 유형 라벨 (예: "픽업", "테이블") */
  order_type_label?: string | null;
  table_number?: string | null;
  status: OrderStatus;
  /** 주문 상태 라벨 (한국어) */
  status_label?: string | null;
  /** 서버에서 제공하는 상태 색상 */
  status_color?: string | null;
  items: OrderItemDetail[];
  /**
   * GROSS 합계 (IVA 포함) — DB 컬럼 `orders.subtotal` 값.
   * olulo-mx-admin#1137 breaking change: `subtotal` → `gross_subtotal` 로 필드명 명확화.
   */
  gross_subtotal: number;
  /**
   * NET 소계 (세금 역산 후, GROSS인 `gross_subtotal`과 구분).
   * BE `OrderResource`에서 GROSS 세금 역산 정책(olulo-mx-admin#1000) 반영 후 노출.
   * 영수증 Subtotal 표시에 사용(#284). olulo-mx-admin#1137 이후 항상 제공되므로
   * 구버전 BE 응답 호환성은 없음 — 어드민 #1137 배포 이후에만 유효.
   */
  net_subtotal: number;
  /** 세율 (퍼센트) */
  tax_rate?: number;
  tax: number;
  tip?: number | null;
  total: number;
  currency: string;
  customer_name?: string | null;
  customer_phone?: string | null;
  /** 비회원 전화번호 */
  guest_phone?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  /** 예상 준비 시간 */
  estimated_ready_time?: string | null;
  /** 주문 확인 시간 */
  confirmed_at?: string | null;
  /** 준비 시작 시간 */
  preparing_at?: string | null;
  /** 준비 완료 시간 */
  ready_at?: string | null;
  /** 주문 완료 시간 */
  completed_at?: string | null;
  /** 처리 완료 시간 */
  processed_at?: string | null;
  /** 취소 사유 */
  cancellation_reason?: string | null;
  /** 취소 시간 */
  cancelled_at?: string | null;
  /** 취소 주체 (예: 'customer', 'store', 'system') */
  cancelled_by?: string | null;
  /** 취소 주체 라벨 (예: "고객", "매장") */
  cancelled_by_label?: string | null;
  /** 환불 상태 */
  refund_status?: 'none' | 'pending' | 'partial' | 'full' | 'not_required' | 'processing' | 'completed' | 'failed' | null;
  /** 환불 상태 라벨 */
  refund_status_label?: string | null;
  /** 결제 실패 사유 */
  failure_reason?: string | null;
  /** 상태 변경 이력 */
  status_history?: OrderStatusHistory[];
  /** 주문 처리 중 여부 */
  is_processing?: boolean;
  /** 활성 주문 여부 */
  is_active?: boolean;
  /** 고객이 취소 가능한지 여부 */
  is_customer_cancellable?: boolean;
  /**
   * 주문 단위 결제 상태 (#854).
   * 분할 결제에서는 개별 latest_payment가 주문 전체의 수납 상태를 대표하지 못한다.
   */
  payment_state?: 'paid' | 'partially_paid' | 'unpaid' | null;
  /** 주문 단위 미수 금액. API decimal은 문자열로 전달된다. */
  payment_outstanding_amount?: string | null;
  /** 주문 항목 수 */
  item_count?: number;
  /** 매장 정보 */
  store?: StoreSummary;
  /** 픽업 위치 안내 (예: "1층 카운터", "주차장 앞") */
  pickup_location?: string | null;
  /**
   * 최근 결제 레코드 요약 (C2 #590).
   * null 이면 결제 레코드 없음 (배지 미표시).
   */
  latest_payment?: LatestPaymentRecord | null;
  /** 비회원이 회원이었다면 얻었을 포인트. 완료/영수증 가입 유인용 서버 권위 값. */
  membership_reward_preview?: {
    earn_rate: number;
    missed_points: number;
    retroactive_credit_available?: boolean;
  } | null;
}

/**
 * 주문 상태 변경 이력
 * @see olulo-mx-admin/app/Http/Resources/OrderStatusHistoryResource.php:39-46
 */
export interface OrderStatusHistory {
  id: number;
  from_status: OrderStatus | null;
  to_status: OrderStatus;
  /** 변경 주체 라벨 (예: "고객", "매장", "시스템") */
  changed_by: string;
  reason: string | null;
  changed_at: string;
}

export interface StoreSummary {
  id: number;
  name: string;
  slug?: string | null;
  /** 픽업 위치 안내 (예: "1층 카운터", "주차장 앞") */
  pickup_location?: string | null;
}

/** 주문 상태 상수 배열 */
export const ORDER_STATUSES = [
  'queued',
  'pending',
  'confirmed',
  'preparing',
  'ready',
  'completed',
  'abandoned',
  'cancelled',
  'failed',
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

/**
 * 활성 주문 상태 (폴링 대상).
 *
 * #659 P2 정합 박제 (2026-06-10 admin 실측): admin `App\Enums\OrderStatus::isActive()`
 * (queued/pending/confirmed/preparing/ready) 와 1:1 일치. admin enum 에 no_show/expired
 * 같은 case 는 없음 — 노쇼/만료는 주문 status 가 아니라 **테이블 세션** 레벨 상태.
 * admin 은 isFinal()/isActive() 를 exhaustive match 로 강제하므로 BE 에 새 case 추가 시
 * 본 상수(+TERMINAL_ORDER_STATUSES)도 함께 점검할 것.
 */
export const ACTIVE_ORDER_STATUSES: readonly OrderStatus[] = [
  'queued',
  'pending',
  'confirmed',
  'preparing',
  'ready',
] as const;

/**
 * 종료 주문 상태.
 * #659 P2: admin `OrderStatus::isFinal()` (completed/abandoned/cancelled/failed) 와 1:1 일치.
 */
export const TERMINAL_ORDER_STATUSES: readonly OrderStatus[] = [
  'completed',
  'abandoned',
  'cancelled',
  'failed',
] as const;

/** 활성 주문 상태인지 확인 */
export function isActiveOrderStatus(status: OrderStatus): boolean {
  return (ACTIVE_ORDER_STATUSES as readonly string[]).includes(status);
}

/** 타입 가드: 유효한 주문 상태인지 확인 */
export function isOrderStatus(value: unknown): value is OrderStatus {
  return typeof value === 'string' && (ORDER_STATUSES as readonly string[]).includes(value);
}

/**
 * 주문 상태 폴링 응답 데이터
 * BE OrderController::status()는 data 래퍼 없이 직접 반환
 */
export interface OrderStatusData {
  id: number;
  order_number: string | null;
  /** #1919 P4: 영업일 내 POS 짧은 순번 — legacy 는 null/부재 */
  daily_sequence?: number | null;
  status: OrderStatus;
  status_label: string;
  /** 주문 처리 중 여부 */
  is_processing: boolean;
  /** 활성 주문 여부 */
  is_active: boolean;
  /** 결제 실패 사유 */
  failure_reason: string | null;
  estimated_ready_time: string | null;
  processed_at: string | null;
  updated_at: string;
}

/** 주문 상태 폴링 응답 (BE는 data 래퍼 없이 직접 반환) */
export type OrderStatusResponse = OrderStatusData;

export interface OrderItemDetail {
  id: number;
  menu_id: number;
  menu_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  /** 옵션 스냅샷 (JSON, 하위 호환용) */
  options?: OrderItemOptionDetail[] | null;
  /** 정규화된 선택 옵션 목록 */
  selected_options?: OrderItemSelectedOption[];
  special_instructions?: string | null;
}

export interface OrderItemOptionDetail {
  option_name: string;
  choices: string[];
  price_modifier: number;
}

/** 메뉴 옵션 입력 타입 */
export type MenuOptionInputType =
  | 'single'
  | 'multiple'
  | 'required_single'
  | 'required_multiple'
  | 'text_input'
  | 'number_input'
  | 'textarea';

/** 정규화된 선택 옵션 (응답용) */
export interface OrderItemSelectedOption {
  id: number;
  menu_option_group_id: number;
  menu_option_id: number | null;
  group_name: string;
  option_name: string | null;
  input_value: string | null;
  price: number;
  input_type: MenuOptionInputType;
  is_selection_type: boolean;
  display_value: string;
}

export interface OrderResponse {
  data: Order;
}

export interface OrderListResponse {
  data: Order[];
  /** Laravel paginate() links */
  links?: PaginationLinks;
  /** Laravel paginate() meta (실제 페이지네이션 정보) */
  meta?: PaginationMeta;
  /** @deprecated meta 사용 */
  pagination?: PaginationInfo;
}

// ==================== 결제 (Payment) ====================

/**
 * 주문의 최근 결제 레코드 요약 (BE OrderResource `latest_payment` 필드).
 *
 * C2 (#590): 주문내역 카드 결제상태 배지 표시에 사용.
 * - `timing === 'postpay' && status === 'pending'` → 미결제 후불
 * - `is_paid` 또는 `status === 'paid'` → 결제완료
 */
export interface LatestPaymentRecord {
  /**
   * 결제 레코드 ID (C3 #590) — 후불 "지금 결제" `POST .../payments/{payment}/charge` 경로 식별자.
   *
   * admin `OrderResource.latest_payment` 가 `id` 를 노출하면 매핑된다. 현재(A2/A3 시점)
   * 미노출이라 구버전 BE 호환 위해 optional. 누락 시 FE 는 "지금 결제" 진입을 차단한다
   * (charge 경로 구성 불가 — paymentId 부재).
   */
  id?: number;
  /** 결제 상태 */
  status: PaymentStatus;
  /** 결제 완료 여부 */
  is_paid: boolean;
  /** 결제 시점 축 (prepay/postpay) — null 이면 미설정 */
  timing: PaymentTimingKind | null;
  /** 결제 수단 조합 코드 (예: 'card_platform_prepay') */
  combination_code: string | null;
  /** 결제 완료 시각 (ISO 8601) */
  paid_at: string | null;
}

/**
 * 결제 수단 타입
 */
export type PaymentMethod = 'card' | 'cash' | 'transfer' | 'wallet';

/**
 * 결제 상태 타입
 */
export type PaymentStatus =
  | 'pending'
  | 'processing'
  | 'paid'
  | 'failed'
  | 'cancelled'
  | 'refunded'
  | 'partial_refund';

/**
 * 결제 생성 요청
 */
export interface CreatePaymentRequest {
  /**
   * 결제 수단 (legacy FE 하위호환 — PaymentModal 경로).
   * 신규 FE (β-2b CheckoutPage) 는 `combination_id` 사용.
   * admin BE `required_without:combination_id` 계약상 둘 중 하나만 있으면 유효.
   */
  method?: PaymentMethod;
  /**
   * 결제 수단 조합 ID (β-2b 신규 — CheckoutPage 조합 선택 UI).
   * 매장이 허용한 활성 조합만 AllowedForStore rule 통과.
   */
  combination_id?: number;
  guest_password?: string;
  /** 포인트 사용 금액 (MXN 단위) */
  points_amount?: number;
  /** 저장된 카드 ID (카드 결제 시) */
  saved_card_id?: number;
}

/**
 * 결제 정보
 */
export interface Payment {
  id: number;
  order_id: number;
  method: PaymentMethod;
  method_label: string;
  provider: 'bloomin' | 'manual';
  status: PaymentStatus;
  status_label: string;
  amount: string;
  currency: string;
  points_amount?: number;
  refund_amount: string;
  refundable_amount: string;

  // 카드 결제 정보
  checkout_id?: string;
  card_brand?: string | null;
  card_last_four?: string | null;

  // 상태 플래그
  is_paid: boolean;
  is_successful: boolean;
  is_refundable: boolean;
  is_cancellable: boolean;
  is_final: boolean;
  is_checkout_expired: boolean;
  requires_manual_confirmation: boolean;

  // 실패 사유
  failure_reason?: string | null;

  // 결제 요약
  summary: string;

  // 타임스탬프
  paid_at?: string | null;
  failed_at?: string | null;
  refunded_at?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * 결제 생성 응답
 */
export interface PaymentCreateResponse {
  data: Payment;
  gateway?: 'bloomin';
  bloomin?: {
    requires_card_input: boolean;
    initiate_url?: string;
    challenge_url?: string | null;
    reference3D?: string | null;
    transaccionId?: string | null;
  };
}

export interface BloominPaymentInitiateRequest {
  guest_password?: string;
  tarjeta: string;
  mes: string;
  anio: string;
  digitos: string;
  titular: string;
}

/**
 * 후불 "지금 결제" 카드 결제 요청 (C3 #590).
 * admin A2 `POST /api/v1/orders/{order}/payments/{payment}/charge` body.
 *
 * 비회원은 `guest_password` 동봉.
 *
 * @see olulo-mx-admin A2 — POST /orders/{order}/payments/{payment}/charge
 */
export interface ChargePaymentRequest {
  /**
   * 카드 번호 (숫자만, 공백 제거).
   *
   * SP2-2b: 정산-시점 보정치(포인트+쿠폰)로 청구액이 0원이 되면 카드 없이 결제가
   * 확정되므로 빈 문자열("")을 허용한다. admin SP2-2a 가 chargeAmount==0 이면
   * 카드 미요구(cardless) — 빈 카드 5필드 + 보정치만 전송.
   */
  tarjeta: string;
  /** 만료 월 (2자리, 예: "03"). 0원 cardless 시 빈 문자열 */
  mes: string;
  /** 만료 연도 (4자리, 예: "2027"). 0원 cardless 시 빈 문자열 */
  anio: string;
  /** CVV (3~4자리). 0원 cardless 시 빈 문자열 */
  digitos: string;
  /** 카드 소유자명 (대문자). 0원 cardless 시 빈 문자열 */
  titular: string;
  /** 비회원 주문 비밀번호 */
  guest_password?: string;
  /**
   * 정산-시점 팁 (절대 금액, MXN) — SP2-2b.
   * admin SP2-1 이 order.tip UPDATE + order.total 재계산. 서버 권위.
   */
  tip_amount?: number;
  /**
   * 사용 포인트 (정수, ≥1) — SP2-2b. 게스트(비회원)는 사용 불가.
   * admin SP2-1 이 잔액·최소·최대 검증 후 차감. 서버 권위.
   */
  points_amount?: number;
  /**
   * 적용 쿠폰 (CustomerCoupon.id) — SP2-2b.
   * admin SP2-1 이 유효성(만료/최소금액/소유) 검증 후 할인 적용. 서버 권위.
   */
  coupon_id?: number;
}

/**
 * 후불 "지금 결제" 카드 결제 응답 — admin snake_case → FE camelCase 정규화 후 shape (C3 #590).
 *
 * admin BE 가 `bloomin.challenge_url` / `bloomin.reference_3d` (snake) 로 내려보내면
 * `chargePayment` 가 `challengeUrl` / `reference3D` (camel) 로 정규화한다.
 * BloominPaymentResult (`{challengeUrl, reference3D}`) 와 동형으로 T2 모달이 소비한다.
 *
 * @see ChargePaymentRequest
 */
export interface ChargePaymentResponse {
  payment: Payment;
  gateway?: 'bloomin';
  bloomin?: {
    /** 3DS 챌린지 redirect URL (admin `challenge_url` 정규화) */
    challengeUrl: string;
    /** 3DS 참조 (admin `reference_3d` 정규화) */
    reference3D?: string | null;
  };
}

/**
 * 후불 합산청구 번들 상태 (#1705 R8) — admin `PostpayChargeBundleStatus` enum 미러.
 *
 * @see olulo-mx-admin App\Enums\PostpayChargeBundleStatus
 */
export type PostpayChargeBundleStatus =
  | 'pending'
  | 'processing'
  | 'paid'
  | 'failed'
  | 'partially_refunded'
  | 'refunded';

/**
 * 후불 합산청구 번들 DTO (#1705 R8) — admin `PostpayChargeBundleData` (#[TypeScript]) 소비 타입.
 *
 * 세션 합산청구(POST /table-sessions/{token}/charge) 응답에서 번들을 식별·표시한다.
 * `order_ids` 는 이 번들이 청구하는 주문 집합(청구 시점 스냅샷) — 성공 후 refetch 로 배지 갱신.
 *
 * @see olulo-mx-admin App\Data\PostpayChargeBundleData
 */
export interface PostpayChargeBundleData {
  id: number;
  table_session_id: number;
  store_id: number;
  status: PostpayChargeBundleStatus;
  status_label: string;
  /** 합산청구 총액 (서버 권위 문자열) */
  total_amount: string;
  /** Bloomin 거래 식별자 (0원 번들은 null) */
  bloomin_transaccion_id: string | null;
  reference_3d: string | null;
  is_successful: boolean;
  is_final: boolean;
  /** 이 번들이 청구한 주문 ID 집합 (청구 시점 스냅샷) */
  order_ids: number[];
  /** Bill 범위 청구면 명시된다. null/부재는 기존 세션 전량 번들. */
  bill_id?: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * 세션 미결제 후불 조회 응답 (#1705 R8).
 * GET /api/v1/table-sessions/{token}/unpaid-postpay.
 *
 * `orders` 는 표준 `OrderResource` 형태(단건 주문 응답과 동일) — `total_amount` 는 서버가
 * 미결제 후불 payment 금액을 합산한 권위 문자열(FE 주장 미신뢰).
 *
 * @see olulo-mx-admin App\Http\Controllers\Api\V1\TableSessionChargeController::unpaidPostpay
 */
export interface SessionUnpaidPostpayResponse {
  orders: Order[];
  count: number;
  /** 미결제 후불 합계 (서버 권위 문자열) */
  total_amount: string;
  /** Bill 범위 quote의 팁 계산 기준 소계. legacy 세션 응답에서는 생략될 수 있다. */
  subtotal_amount?: string;
  /** Bill 범위 quote에 이미 박제된 팁. 재청구 시 새 tip_amount로 교체한다. */
  tip_amount?: string;
  /** exact Bill quote stale-write precondition. 서버 값을 변환 없이 POST에 되돌린다. */
  expected_bill_subtotal_minor?: number;
  /** Comensal 대표 pay-all quote stale-write precondition. */
  expected_total_minor?: number;
  /**
   * 이 세션에서 분할결제를 **실제로 쓸 수 있는가** (admin #2326).
   *
   * 매장 정책 AND 개발 게이트를 서버가 합쳐서 내린다. FE 가 매장 플래그만 보고
   * 판단하면, 게이트가 닫힌 환경에서 좌석 스캔부터 하고 403 을 받는다 — 그 스캔은
   * 쓰지도 않는 매장에 참여자 행을 남긴다.
   *
   * 구버전 서버는 이 필드를 내리지 않으므로 optional 이다. 없으면 **꺼진 것으로 본다**.
   */
  split_payment_available?: boolean;
  /** Bill 범위 quote면 소유 검증을 통과한 Bill. 세션 전량 조회에서는 null. */
  bill?: SessionBill | null;
}

/**
 * 세션 합산청구 주문별 보정치 입력 (#1705 R8).
 * `adjustments[order_id] = {tip_amount?, points_amount?, coupon_id?}` 형태로 body 에 동봉.
 */
export interface ChargeSessionAdjustment {
  /** 정산-시점 팁 (절대 금액, MXN) */
  tip_amount?: number;
  /** 사용 포인트 (정수, ≥1) — 게스트(비회원)는 사용 불가 */
  points_amount?: number;
  /** 적용 쿠폰 (CustomerCoupon.id) */
  coupon_id?: number;
}

/**
 * 세션 합산청구 카드/보정치 요청 (#1705 R8).
 * POST /api/v1/table-sessions/{token}/charge body.
 *
 * 카드 5필드는 단건 charge 와 동일 — 0원 번들(보정치로 전액 커버)이면 빈 문자열 허용
 * (서버가 reserve 후 chargeAmount > 0 일 때만 카드 required 판정 — 서버 권위).
 * 비회원은 `guest_password` 를 body 로 동봉(GET 의 헤더와 정합 — R5).
 *
 * @see olulo-mx-admin App\Http\Requests\Api\V1\ChargeSessionBundleRequest
 */
export interface ChargeSessionRequest {
  /** 카드 번호 (숫자만, 공백 제거). 0원 번들 cardless 시 빈 문자열 */
  tarjeta: string;
  /** 만료 월 (2자리). 0원 번들 시 빈 문자열 */
  mes: string;
  /** 만료 연도 (4자리). 0원 번들 시 빈 문자열 */
  anio: string;
  /** CVV (3~4자리). 0원 번들 시 빈 문자열 */
  digitos: string;
  /** 카드 소유자명 (대문자). 0원 번들 시 빈 문자열 */
  titular: string;
  /** 지정 시 내 Bill만 청구한다. 생략은 명시적 `Yo pago todo` 세션 전량 경로다. */
  bill_id?: number;
  /** exact Bill quote의 팁 제외 subtotal precondition. */
  expected_bill_subtotal_minor?: number;
  /** Comensal 대표 pay-all quote의 session total precondition. */
  expected_total_minor?: number;
  /** 비회원 주문 비밀번호 (POST body — R5 정합) */
  guest_password?: string;
  /** 주문별 보정치 — order_id 키 맵 (string 키로 직렬화). `session_adjustments` 와 배타. */
  adjustments?: Record<string, ChargeSessionAdjustment>;
  /**
   * 세션(번들) 단위 보정치 (#1857 additive — admin 이 주문별 비례 배분).
   *
   * N건 합산결제에서 귀속 주문을 특정할 수 없는 팁을 세션 전체에 부여한다.
   * 주문별 `adjustments` 와 **동시 전송 금지(배타)** — 단건은 기존 adjustments 경로 유지.
   * admin #1857 머지·배포 전에는 전송하지 않는다(FE feature-gate, SessionChargePanel 참조).
   */
  session_adjustments?: SessionBundleAdjustment;
}

/**
 * 세션(번들) 단위 보정치 (#1857) — N건 합산결제 팁. admin 이 주문별 비례 배분한다.
 */
export interface SessionBundleAdjustment {
  /** 정산-시점 팁 (절대 금액, MXN) — 세션 전체 부여 */
  tip_amount?: number;
}

/**
 * 세션 합산청구 응답 — admin snake_case → FE camelCase 정규화 후 shape (#1705 R8).
 *
 * 단건 charge 응답과 일관(번들 식별자로 식별). admin `bloomin.challenge_url` / `reference_3d`
 * (snake) 를 `challengeUrl` / `reference3D` (camel) 로 정규화한다(단건 ChargePaymentResponse 동형).
 *
 * @see ChargeSessionRequest
 */
export interface ChargeSessionResponse {
  bundle: PostpayChargeBundleData;
  bloomin?: {
    /** 3DS 챌린지 redirect URL (admin `challenge_url` 정규화) */
    challengeUrl: string;
    /** 3DS 참조 (admin `reference_3d` 정규화) */
    reference3D?: string | null;
  };
}

export interface BloominPaymentInitiateResult {
  payment_id: number;
  transaccionId: string;
  challengeUrl: string;
  reference3D?: string | null;
}

export interface BloominPaymentInitiateResponse {
  success: boolean;
  data?: BloominPaymentInitiateResult;
  error?: string;
}

/**
 * 결제 조회 응답
 */
export interface PaymentResponse {
  data: Payment;
}

/**
 * 결제 상태 폴링 응답
 */
export interface PaymentStatusResponse {
  id: number;
  status: PaymentStatus;
  status_label: string;
  is_final: boolean;
  is_successful: boolean;
  method: PaymentMethod;
  amount?: string;
  message?: string;
  paid_at?: string | null;
  failure_reason?: string | null;
  updated_at: string;
  /** #717: 주문 식별자 — 결제완료 화면 짧은 순번 표시(#1919 P4 후속). */
  order_number?: string | null;
  daily_sequence?: number | null;
  membership_reward_preview?: {
    earn_rate: number;
    missed_points: number;
    retroactive_credit_available?: boolean;
  } | null;
}

/**
 * 결제 취소 요청
 */
export interface CancelPaymentRequest {
  guest_password?: string;
  reason?: string;
}

/**
 * 결제 취소 응답
 */
export interface CancelPaymentResponse {
  data: Payment;
  message: string;
}

// ==================== 인증 (Auth) ====================
/**
 * Firebase 로그인 요청
 * POST /api/customer/auth/firebase/login
 */
export interface FirebaseLoginRequest {
  idToken: string;
}

// ==================== WhatsApp 인증 ====================
// @CODE:WHATSAPP-AUTH-001 | SPEC: .claude/specs/SPEC-137-whatsapp-auth.md

/**
 * WhatsApp OTP 발송 요청
 * POST /api/v1/auth/whatsapp/send-otp
 */
export interface SendWhatsAppOtpRequest {
  phone_number: string;
  /** 앱 선택 언어 — 15개 언어 OTP 템플릿 선택 (admin #2122). 부재 시 서버가 Accept-Language 협상. */
  locale?: string;
}

/**
 * WhatsApp OTP 발송 응답
 */
export interface SendWhatsAppOtpResponse {
  success: boolean;
  message?: string;
  expires_at?: string;
  retry_after?: number;
  error?: string;
}

/**
 * WhatsApp OTP 검증 요청
 * POST /api/v1/auth/whatsapp/verify-otp
 */
export interface VerifyWhatsAppOtpRequest {
  phone_number: string;
  otp: string;
}

/**
 * WhatsApp OTP 검증 응답
 */
export interface VerifyWhatsAppOtpResponse {
  success: boolean;
  message?: string;
  user?: AuthUser;
  is_new_user?: boolean;
  error?: string;
  attempts_remaining?: number;
  retry_after?: number;
}

/**
 * WhatsApp 인증 상태 응답
 * GET /api/v1/auth/whatsapp/status
 */
export interface WhatsAppAuthStatusResponse {
  authenticated: boolean;
  user: AuthUser | null;
}

/**
 * 인증된 사용자 정보
 */
/**
 * 고객 성별 (admin#1060 Phase 1 Gender enum 4 case 정합)
 *
 * - male / female / other / prefer_not_to_say
 * - other 는 소수자 통합 분류 (마케팅/통계 보호 대상)
 * - prefer_not_to_say 는 LFPDPPP 데이터 최소 수집 옵션
 */
export type Gender = 'male' | 'female' | 'other' | 'prefer_not_to_say';

export interface AuthUser {
  id: number;
  name: string;
  email: string | null;
  phone_number: string | null;
  avatar_url: string | null;
  birthday: string | null;
  gender: Gender | null;
}

/**
 * Firebase 로그인 응답
 */
export interface FirebaseLoginResponse {
  success: boolean;
  message: string;
  user?: AuthUser;
  error?: string;
}

/**
 * 로그아웃 응답
 * POST /api/customer/auth/logout
 */
export interface LogoutResponse {
  success: boolean;
  message: string;
}

/**
 * @deprecated FirebaseLoginResponse 사용
 */
export interface AuthResponse {
  data: {
    user: AuthUser;
    token?: string;
  };
}

// ==================== API 에러 응답 ====================
export interface ApiErrorResponse {
  message: string;
  error_code?: string;
  errors?: Record<string, string[]>;
}

// ==================== 사용자 프로필 (User Profile) ====================

/**
 * 사용자 환경설정
 */
export interface UserPreference {
  language: 'es-MX' | 'en' | 'ko';
  currency: 'MXN' | 'USD';
  notifications_enabled: boolean;
  marketing_accepted: boolean;
  theme: 'light' | 'dark' | 'system';
}

/**
 * 사용자 프로필
 */
export interface UserProfile {
  id: number;
  name: string;
  email: string;
  phone_number: string | null;
  avatar_url: string | null;
  birthday: string | null;
  gender: Gender | null;
  email_verified_at: string | null;
  preference: UserPreference | null;
  companies_count: number;
  created_at: string;
}

/**
 * 사용자 프로필 응답
 */
export interface UserProfileResponse {
  success: boolean;
  data: UserProfile;
}

/**
 * 사용자 프로필 수정 요청
 */
export interface UpdateProfileRequest {
  name?: string;
  phone_number?: string;
  avatar_url?: string | null;
  birthday?: string | null;
  gender?: Gender | null;
}

/**
 * 사용자 환경설정 수정 요청
 */
export interface UpdatePreferenceRequest {
  language?: 'es-MX' | 'en' | 'ko';
  currency?: 'MXN' | 'USD';
  notifications_enabled?: boolean;
  marketing_accepted?: boolean;
  theme?: 'light' | 'dark' | 'system';
}

/**
 * 환경설정 응답
 */
export interface UserPreferenceResponse {
  success: boolean;
  data: UserPreference;
}

/**
 * 고객 회사 정보 (멕시코 RFC용)
 */
export interface CustomerCompany {
  id: number;
  name: string;
  rfc: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  tax_regime: string | null;
  cfdi_use: string | null;
  is_default: boolean;
  created_at: string;
}

/**
 * 회사 목록 응답
 */
export interface CompanyListResponse {
  success: boolean;
  data: CustomerCompany[];
}

/**
 * 회사 상세 응답
 */
export interface CompanyResponse {
  success: boolean;
  data: CustomerCompany;
}

/**
 * 회사 생성/수정 요청
 */
export interface CreateCompanyRequest {
  name: string;
  rfc: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  tax_regime?: string;
  cfdi_use?: string;
  is_default?: boolean;
}

export interface UpdateCompanyRequest {
  name?: string;
  rfc?: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  tax_regime?: string | null;
  cfdi_use?: string | null;
}

// ==================== 메뉴 옵션 API (#174) ====================

/**
 * 옵션 그룹의 개별 선택지 (API 응답)
 */
export interface ApiMenuOptionChoice {
  id: number;
  name: string;
  description: string | null;
  additional_price: number;
  quantity_limit?: number | null;
  is_available: boolean;
  sort_order: number;
}

/**
 * 입력형 옵션의 검증 규칙
 */
export interface ValidationRules {
  min_length?: number;
  max_length?: number;
  min_value?: number;
  max_value?: number;
  pattern?: string;
}

/**
 * 옵션 그룹 (API 응답)
 */
export interface ApiMenuOptionGroup {
  id: number;
  name: string;
  description: string | null;
  input_type: MenuOptionInputType;
  is_required: boolean;
  min_select: number | null;
  max_select: number | null;
  validation_rules?: ValidationRules;
  sort_order: number;
  /** 선택형 옵션에만 포함 */
  options?: ApiMenuOptionChoice[];
}

/**
 * 옵션 그룹 목록 조회 응답
 * GET /api/v1/menus/{menuId}/option-groups
 */
export interface MenuOptionGroupsResponse {
  data: ApiMenuOptionGroup[];
  meta: {
    menu_id: number;
    menu_name: string;
    base_price: number;
    currency: string;
  };
}

/**
 * 개별 옵션 선택 (요청용)
 */
export interface OptionSelection {
  option_group_id: number;
  /** 선택형 옵션의 선택된 옵션 ID 배열 */
  choice_ids?: number[];
  /** 입력형 옵션의 입력 값 */
  input_value?: string;
}

/**
 * 옵션 검증 요청
 * POST /api/v1/menus/{menuId}/validate-options
 */
export interface ValidateOptionsRequest {
  selections: OptionSelection[];
  quantity?: number;
}

/**
 * 검증 에러
 */
export interface OptionValidationError {
  option_group_id: number;
  group_name: string;
  code: string;
  message: string;
}

/**
 * 가격 내역
 */
export interface PriceBreakdown {
  base_price: number;
  options_price: number;
  unit_price: number;
  quantity: number;
  total_price: number;
  currency: string;
}

/**
 * 선택 요약
 */
export interface SelectedSummary {
  group_id: number;
  group_name: string;
  /** 선택형 옵션의 선택된 항목 이름 */
  selections?: string[];
  /** 입력형 옵션의 입력 값 */
  input_value?: string;
}

/**
 * 옵션 검증 응답
 */
export interface ValidateOptionsResponse {
  valid: boolean;
  errors: OptionValidationError[];
  price_breakdown: PriceBreakdown | null;
  selected_summary: SelectedSummary[] | null;
}

// ==================== 알림 (Notifications) ====================
// @CODE:API-201 | SPEC: olulo-mx-admin/.claude/specs/SPEC-201-notification-api.md

/**
 * 알림 타입
 */
export type NotificationType = 'order_status' | 'promotion' | 'system';

/**
 * API 알림 데이터
 */
export interface ApiNotification {
  id: string; // UUID
  notification_type: NotificationType;
  title: string;
  body: string;
  data: {
    order_id?: number;
    order_number?: string;
    status?: string;
    store_name?: string;
    store_id?: number;
    promotion_id?: number;
    url?: string;
    image_url?: string;
    action_url?: string;
    priority?: 'low' | 'normal' | 'high';
    announcement_id?: number;
  };
  read_at: string | null; // ISO 8601
  created_at: string; // ISO 8601
}

/**
 * 알림 목록 응답
 */
export interface NotificationListResponse {
  success: boolean;
  data: ApiNotification[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    unread_count: number;
  };
}

/**
 * 개별 알림 응답
 */
export interface NotificationResponse {
  success: boolean;
  data: ApiNotification;
  message?: string;
}

/**
 * 읽지 않은 알림 수 응답
 */
export interface UnreadCountResponse {
  success: boolean;
  data: {
    count: number;
  };
}

// ==================== 포인트 (Points) ====================
// @CODE:API-202 | SPEC: olulo-mx-admin/.claude/specs/SPEC-202-points-coupons-api.md

/**
 * 포인트 스코프 타입 (3레벨: 조직/브랜드/매장)
 */
export type PointScopeType = 'organization' | 'brand' | 'store';

/** 포인트 breakdown 항목 */
export interface PointBreakdownItem {
  id: number | null;
  name: string | null;
  balance: number;
}

/** 포인트 breakdown (조직/브랜드/매장) — BE는 항상 3개 키 반환 */
export interface PointBreakdown {
  organization: PointBreakdownItem;
  brand: PointBreakdownItem;
  store: PointBreakdownItem;
}

/**
 * 포인트 계좌 (스코프별 잔액)
 */
export interface PointAccount {
  scope_type: PointScopeType;
  scope_id: number;
  scope_name: string;
  scope_image_url: string | null;
  balance: number;
  lifetime_earned: number;
  lifetime_used: number;
  expiring_soon: {
    amount: number;
    date: string | null; // ISO 8601
  };
}

/**
 * 포인트 잔액 응답
 * GET /api/v1/points
 */
export interface PointBalanceResponse {
  success: boolean;
  data: {
    total_balance: number;
    accounts: PointAccount[];
  };
}

/**
 * 매장별 포인트 잔액 응답 (store_id 지정 시)
 * GET /api/v1/points?store_id={storeId}
 */
export interface StorePointBalanceResponse {
  success: boolean;
  data: {
    available: number;
    breakdown: PointBreakdown;
    lifetime: {
      earned: number;
      used: number;
    };
  };
}

/**
 * 매장별 사용 가능 포인트 응답
 * GET /api/v1/stores/{storeId}/points/available
 */
export interface StoreAvailablePointsResponse {
  success: boolean;
  data: {
    total_available: number;
    usable_amount: number;
    min_use_points: number;
    max_use_ratio: number;
    breakdown: PointBreakdown;
  };
}

/**
 * 포인트 거래 내역 타입
 */
export type PointTransactionType = 'earn' | 'use' | 'expire' | 'adjust' | 'revoke';

/**
 * 포인트 거래 원천 (BE PointTransactionSource enum 과 동기화)
 *
 * #1144 리팩터: legacy 'order' 값을 포함해 hydrate 안전 + 신규 분리값 추가.
 * 'order' 는 적립/환불/사용 혼재이므로 분기 시 type·description 함께 확인.
 *
 * @see olulo-mx-admin/app/Enums/PointTransactionSource.php
 */
export type PointTransactionSource =
  | 'order'         // Legacy (적립/환불/사용 혼재)
  | 'order_earn'    // 주문 완료 적립 (OrderStateMachine)
  | 'order_refund'  // 환불 역적립 (PaymentService)
  | 'order_use'     // 결제 시 포인트 사용 (PaymentService)
  | 'revoke'        // 포인트 회수
  | 'admin'         // 관리자 조정
  | 'expire'        // 만료
  | 'manual';       // 기타

/**
 * 포인트 거래 내역
 */
export interface PointTransaction {
  id: number;
  type: PointTransactionType;
  amount: number;
  balance_after: number;
  description: string;
  scope_type: PointScopeType;
  scope_id: number;
  scope_name: string;
  store_name: string | null;
  brand_name: string | null;
  source_type: PointTransactionSource | null;
  source_id: number | null;
  expires_at: string | null; // ISO 8601
  created_at: string; // ISO 8601
}

/**
 * 포인트 내역 응답
 */
export interface PointHistoryResponse {
  success: boolean;
  data: PointTransaction[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

// ==================== 쿠폰 (Coupons) ====================
// @CODE:API-202 | SPEC: olulo-mx-admin/.claude/specs/SPEC-202-points-coupons-api.md

/**
 * 쿠폰 타입
 */
export type CouponType = 'percentage' | 'fixed_amount' | 'free_item' | 'free_delivery';

/**
 * 고객 보유 쿠폰
 */
export interface CustomerCoupon {
  id: number;
  coupon_id: number;
  code: string;
  name: string;
  description: string | null;
  type: CouponType;
  value: number;
  min_order_amount: number | null;
  max_discount_amount: number | null;
  store_name: string | null;
  expires_at: string | null; // ISO 8601
  is_usable: boolean;
  used_at: string | null; // ISO 8601 — null이면 미사용
  claimed_at: string; // ISO 8601
}

/**
 * 쿠폰 목록 응답
 */
export interface CouponListResponse {
  success: boolean;
  data: CustomerCoupon[];
}

/**
 * 쿠폰 등록 요청
 */
export interface RedeemCouponRequest {
  code: string;
}

/**
 * 쿠폰 등록 응답
 */
export interface RedeemCouponResponse {
  success: boolean;
  message: string;
  data: CustomerCoupon;
}

/**
 * 쿠폰 검증 요청
 */
export interface ValidateCouponRequest {
  order_amount: number;
  store_id?: number;
  category_id?: number;
}

/**
 * 쿠폰 검증 응답
 */
export interface ValidateCouponResponse {
  success: boolean;
  message?: string;
  data: {
    valid: boolean;
    discount: number;
    reason?: string;
  };
}

// ==================== 웨이터 호출 ====================

/**
 * 웨이터 호출 상태
 */
export type WaiterCallStatus = 'pending' | 'acknowledged' | 'completed' | 'cancelled';

/**
 * 웨이터 호출 정보
 */
export interface WaiterCall {
  id: number;
  table_number: string;
  status: WaiterCallStatus;
  status_label: string;
  message: string | null;
  acknowledged_at: string | null;
  completed_at: string | null;
  created_at: string;
}

/**
 * 웨이터 호출 선택 항목 (#1761) — 매장 설정 항목(item_id) 또는 기본 카탈로그(default_key).
 * 둘 중 정확히 하나. admin 이 매장 언어로 현지화해 PoS 에 표시한다.
 */
export type WaiterCallRequestItem = { item_id: number } | { default_key: string };

/**
 * 웨이터 호출 요청
 */
export interface CreateWaiterCallRequest {
  table_number: string;
  message?: string;
  /** 자유 입력 message 의 원본 locale. admin 이 POS 표시용 es-MX 번역 스냅샷에 사용한다. */
  message_locale?: string;
  /** 고객 선택 항목 (#1761) — 라벨 접기 폐기, 구조화 전송. 미선택 시 생략. */
  items?: WaiterCallRequestItem[];
}

/**
 * 웨이터 호출 생성 응답
 */
export interface CreateWaiterCallResponse {
  success: boolean;
  message: string;
  data: WaiterCall;
}

/**
 * 웨이터 호출 조회 응답
 */
export interface WaiterCallResponse {
  data: WaiterCall;
}

/**
 * 웨이터 호출 취소 응답
 */
export interface CancelWaiterCallResponse {
  success: boolean;
  message: string;
}

// ==================== 서버 장바구니 (Server Cart) ====================
// @see olulo-mx-admin/app/Http/Resources/CartResource.php
// @see olulo-mx-admin/app/Http/Resources/CartItemResource.php

/**
 * 서버 장바구니
 */
export interface ServerCart {
  store_id: number;
  store: { id: number; slug: string; name: string; address: string } | null;
  cart_items: ServerCartItem[];
  order_type: 'pickup' | 'table';
  table_number: string | null;
  total_item_count: number;
  total_amount: number;
  currency: string;
  created_at: string;
  updated_at: string;
}

/**
 * 서버 장바구니 아이템
 */
export interface ServerCartItem {
  id: number;
  menu_id: number;
  menu: {
    id: number;
    code: string;
    name: string;
    description: string | null;
    image_url: string | null;
    thumb_url: string | null;
    /**
     * 메뉴 가용성 정보 (#1128: BE PR #1129).
     * MenuResource.is_available_at과 동일 스키마. FE 카트 편집 모달
     * "판매 시간" 행(#280) 렌더링용.
     */
    is_available_at?: MenuAvailabilityInfo | null;
  };
  quantity: number;
  unit_price: number;
  options_price: number;
  currency: string;
  subtotal: number;
  selected_options: Record<string, string[]> | null;
  /**
   * #502 (admin#1500): 입력형 옵션 (number_input/text_input/textarea) 사용자
   * 입력 값. cart 재수화 (로그인 직후 / 새로고침 / 다른 기기 진입) 시 보존.
   */
  input_values: Record<string, string> | null;
  special_request: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * 장바구니 아이템 추가 요청
 */
export interface AddCartItemRequest {
  menu_id: number;
  quantity: number;
  selected_options?: Record<string, string[]>;
  /** #502: 입력형 옵션 사용자 입력 값 */
  input_values?: Record<string, string>;
  special_request?: string;
}

/**
 * 장바구니 아이템 수정 요청
 */
export interface UpdateCartItemRequest {
  quantity?: number;
  selected_options?: Record<string, string[]>;
  /** #502: 입력형 옵션 사용자 입력 값 */
  input_values?: Record<string, string>;
  special_request?: string;
}

// ==================== 매장 영업시간 상세 ====================

/** 영업시간 유형 (BE 5단계 우선순위: custom > holiday > weekday > weekend > default) */
export type StoreHoursType = 'custom' | 'holiday' | 'weekday' | 'weekend' | 'default';

/** 영업 시간대 구간 */
export interface StoreHoursSegment {
  open: string;  // HH:MM
  close: string; // HH:MM
}

/** 날짜별 영업시간 항목 (index 응답) */
export interface StoreHoursEntry {
  date: string;       // YYYY-MM-DD
  day_name: string;   // 현지화된 요일명
  is_closed: boolean;
  type: StoreHoursType;
  segments: StoreHoursSegment[];
  label?: string | null; // "크리스마스 특별 영업" 등
}

/** 매장 영업시간 목록 응답 (GET /api/v1/stores/{id}/hours) */
export interface StoreHoursResponse {
  store_id: number;
  store_name: string;
  hours: StoreHoursEntry[];
}

/** 현재 적용 중인 영업시간 상세 */
export interface EffectiveHours {
  type: StoreHoursType;
  day_of_week: number | null;
  open_time_1: string | null; // HH:MM:SS
  close_time_1: string | null;
  open_time_2: string | null;
  close_time_2: string | null;
  is_closed: boolean;
  label: string | null;
}

/** 매장 영업 여부 확인 응답 (GET /api/v1/stores/{id}/hours/check) */
export interface StoreOpenCheckResponse {
  store_id: number;
  store_name: string;
  is_open: boolean;
  checked_at: string;          // ISO 8601
  store_timezone: string;
  store_local_time: string;    // ISO 8601
  client_timezone: string;
  reason: StoreHoursType;
  effective_hours: EffectiveHours | null;
  /** 영업 중일 때만 존재 */
  current_segment?: number | null;
  /** 영업 중일 때 다음 마감 시각 */
  next_close_time?: string | null;
  /** 휴무 중일 때 다음 오픈 시각 */
  next_open_time?: string | null;
}

/**
 * @deprecated StoreHoursResponse 사용
 */
export interface StoreHoursDetail {
  store_id: number;
  store_name: string;
  hours: StoreHours;
  timezone: string;
}

// ==================== 저장된 카드 (SavedCard) ====================
// @CODE:API-302 | SPEC: olulo-mx-admin/.claude/specs/SPEC-302-payment-advanced-features.md

/**
 * 카드 브랜드 타입
 */
export type CardBrand = 'VISA' | 'MASTERCARD' | 'AMEX' | 'UNKNOWN';

/**
 * 저장된 카드 정보
 * API: GET /api/v1/payment-methods
 */
export interface SavedCard {
  id: number;
  provider?: 'bloomin';
  card_brand: CardBrand;
  card_last_four: string;
  card_holder: string | null;
  expiry_month: string;
  expiry_year: string;
  /** 만료일 표시 문자열 (예: "12/26") */
  expiry_display: string;
  is_default: boolean;
  is_active: boolean;
  is_expired: boolean;
  is_usable: boolean;
  nickname: string | null;
  /** 표시 이름 (별명 또는 마스킹된 카드 번호) */
  display_name: string;
  /** 마스킹된 카드 표시 (예: "VISA ****1234") */
  masked_display: string;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * 저장된 카드 목록 응답
 * BE PaymentMethodController::index()는 ResourceCollection { data: [...] } 반환
 */
export interface SavedCardListResponse {
  data: SavedCard[];
}

/**
 * 저장된 카드 단일 응답
 * BE는 { data, message? } 반환 (success 없음)
 */
export interface SavedCardResponse {
  data: SavedCard;
  message?: string;
}

export type CardRegistrationGateway = 'bloomin';

export interface CardRegistrationSession {
  gateway: CardRegistrationGateway;
  checkoutId?: string;
  requiresCardInput?: boolean;
}

export interface BloominCardRegistrationPayload {
  tarjeta: string;
  mes: string;
  anio: number | string;
  digitos: string;
  titular: string;
}

/**
 * 카드 등록 체크아웃 생성 응답
 * POST /api/v1/payment-methods
 * BE는 { data, message } 반환 (success 없음)
 */
export interface CardCheckoutResponse {
  data: {
    gateway?: CardRegistrationGateway;
    checkout_id?: string;
    requires_card_input?: boolean;
  };
  message?: string;
}

/**
 * 카드 등록 확인 요청
 * POST /api/v1/payment-methods/confirm
 */
export type BloominConfirmCardRegistrationRequest = BloominCardRegistrationPayload & {
  nickname?: string;
};

export type ConfirmCardRegistrationRequest = BloominConfirmCardRegistrationRequest;

/**
 * 카드 별명 업데이트 요청
 * PATCH /api/v1/payment-methods/{id}
 */
export interface UpdateCardNicknameRequest {
  nickname: string | null;
}

/**
 * 카드 삭제 응답
 * BE PaymentMethodController::destroy()는 { message } 반환
 */
export interface DeleteCardResponse {
  message: string;
}

/**
 * 기본 카드 설정 응답
 * BE PaymentMethodController::setDefault()는 { data, message } 반환
 */
export interface SetDefaultCardResponse {
  data: SavedCard;
  message: string;
}

// ==================== 테이블 세션 (TableSession) ====================
// @see Issue #220, #221 - 세션 타이머/연장/종료 UX

/** 테이블 세션 상태 */
export type TableSessionStatus = 'active' | 'completed' | 'expired' | 'cancelled';

/** 테이블 세션 테이블 정보 */
export interface TableSessionTable {
  id: number;
  table_number: string;
  table_name: string | null;
  display_name: string;
  capacity: number;
  store: {
    id: number;
    name: string;
    slug: string;
    nickname: string | null;
  };
}

/**
 * TableSessionResource (BE 응답)
 * GET /api/v1/sessions/{sessionToken}
 * POST /api/v1/sessions/{sessionToken}/extend
 * POST /api/v1/sessions/{sessionToken}/end
 */
export interface TableSessionResource {
  id: number;
  status: TableSessionStatus;
  status_label: string;
  status_color: string;
  guest_count: number;
  started_at: string;
  expires_at: string;
  ended_at: string | null;
  duration_minutes: number;
  is_active: boolean;
  is_expired: boolean;
  extend_count: number;
  max_extend_count: number;
  can_extend_more: boolean;
  table?: TableSessionTable;
  orders?: Order[];
  orders_count?: number;
}

/** 세션 조회 응답 */
export interface TableSessionResponse {
  data: TableSessionResource;
}

/** 세션 연장 성공 응답 */
export interface TableSessionExtendResponse {
  data: TableSessionResource;
  message: string;
}

/** 세션 종료 성공 응답 */
export interface TableSessionEndResponse {
  data: TableSessionResource;
  message: string;
}

export interface EstimatePointsRequest {
  gross_amount: number;
  tip_amount: number;
  points_used: number;
}

export interface EstimatePointsResponse {
  success: boolean;
  data: {
    estimated_points: number;
    earn_rate: number;
    note: string | null;
  };
}

/**
 * LFPDPPP 동의 카테고리 (#372 / admin#1361)
 * - primary: 서비스 운영 / 결제 처리 / ARCO 권리 — 필수
 * - secondary: 마케팅 / 통계 — 선택 (옵트인)
 */
export type ConsentCategory = 'primary' | 'secondary';

/**
 * 동의 상태 값 (BE app/Enums/ConsentStatus enum 정합)
 * - granted: 동의함
 * - withdrawn: 철회 / 미동의
 */
export type ConsentStatusValue = 'granted' | 'withdrawn';

/**
 * 동의 채널 (BE StoreConsentRequest::rules() Rule::in 정합)
 * - banner: 재동의 배너
 * - settings: 프로필/설정 페이지 토글
 * - api: 시스템 자동 호출 (회원가입 등)
 */
export type ConsentChannel = 'banner' | 'settings' | 'api';

/**
 * 단일 동의 레코드 (admin#1365 BE consents 테이블 응답 정합).
 * #424: BE ConsentController response 형태 (status enum + consented_at/via).
 * granted boolean + recorded_at 폐기.
 */
export interface ConsentRecord {
  /** POST 응답에만 포함 (GET 응답 없음) */
  id?: number;
  category: ConsentCategory;
  status: ConsentStatusValue;
  /** 동의 시점에 적용된 Aviso 정책 버전 */
  policy_version: string;
  /** ISO 8601 — BE 가 항상 반환 (동의/철회 시각) */
  consented_at: string;
  /** 동의 채널 (banner / settings / api) */
  consented_via: ConsentChannel;
}

/**
 * 동의 상태 조회 응답
 * GET /api/v1/customer/consents
 *
 * #424: BE 응답 형태 정합 — `policy_version` 키 (이전 `current_policy_version` 오정의).
 */
export interface ConsentStatusResponse {
  success?: boolean;
  data: {
    /** 현재 활성 정책 버전 (BE 응답 키 정합) */
    policy_version: string;
    /**
     * 정책 버전 변경 등으로 재동의가 필요한지 여부.
     * true 시 FE 는 재동의 배너를 강제 모달로 표시.
     */
    needs_reconsent: boolean;
    consents: ConsentRecord[];
  };
}

/**
 * 동의 기록 / 갱신 요청
 * POST /api/v1/customer/consents
 *
 * #424: BE 가 status (enum) + via (in) 를 obligatorio 로 받음. granted boolean 폐기.
 */
export interface RecordConsentRequest {
  category: ConsentCategory;
  status: ConsentStatusValue;
  via: ConsentChannel;
}

export interface RecordConsentResponse {
  /** BE 가 항상 true 반환 (FE 필수 검증 안 하므로 optional) */
  success?: boolean;
  data: ConsentRecord;
  message?: string;
}

/**
 * Store별 파트너 개인정보 제공 동의 상태.
 *
 * 목록 및 갱신 응답은 서버가 판정한 exact Store eligibility/capability를
 * 그대로 사용하며, 클라이언트가 주문 이력으로 재계산하지 않는다.
 */
export type PartnerVisibilityStatus = 'granted' | 'withdrawn';

export type PartnerVisibilityAvailability = 'active' | 'historical';

export interface PartnerVisibilityNotice {
  version: string;
  url: string;
}

export interface PartnerVisibilityStore {
  store_id: number;
  store_name: string;
  availability: PartnerVisibilityAvailability;
  recorded_status: PartnerVisibilityStatus | null;
  effective_status: PartnerVisibilityStatus;
  consent_version: string | null;
  consented_at: string | null;
  can_grant: boolean;
  can_withdraw: boolean;
}

export interface PartnerVisibilityListResponse {
  success: true;
  data: {
    notice: PartnerVisibilityNotice;
    stores: PartnerVisibilityStore[];
    next_cursor: string | null;
  };
}

export interface PartnerVisibilityRecordResponse {
  success: true;
  data: {
    notice: PartnerVisibilityNotice;
    store: PartnerVisibilityStore;
  };
}

/**
 * 계정 전역 SECONDARY 마케팅 동의 projection.
 *
 * Store별 PARTNER_VISIBILITY와 별개의 동의이며 capability는 서버가 판정한다.
 */
export type MarketingConsentStatus = 'granted' | 'withdrawn';

export interface MarketingConsentNotice {
  version: string;
  url: string;
}

export interface MarketingConsentProjection {
  recorded_status: MarketingConsentStatus | null;
  effective_status: MarketingConsentStatus;
  consent_version: string | null;
  status_changed_at: string | null;
  can_grant: boolean;
  can_withdraw: boolean;
}

export interface MarketingConsentResponse {
  success: true;
  data: {
    notice: MarketingConsentNotice | null;
    consent: MarketingConsentProjection;
  };
}

export interface UpdateMarketingConsentRequest {
  status: MarketingConsentStatus;
  observed_notice_version: string;
}
